import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button, Modal, Container, Row, Col, Alert } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import {
  useAppContext,
  callNativeMobileApp,
  useTranslator,
} from '@front-activerse/common-components';
import { findCameraWithTorch } from '../helpers';

const ScanPlateContainer: React.FC = () => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const [isTorchOn, setIsTorchOn] = useState(false);
  const [isTorchSupported, setIsTorchSupported] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCameraId, setSelectedCameraId] = useState<string>('');
  const [isSearchingCamera, setIsSearchingCamera] = useState(false);
  const scanPlateDetailsPath = '/tracker/scan-plate/details';
  const [showModal, setShowModal] = useState(false);

  const { t } = useTranslator();

  const {
    images: {
      cameraShutter,
      closeWhiteIcon,
      flashOnIcon,
      flashOffIcon,
      infoWhiteIcon,
      photoGalleryIcon,
      trackerScanFoodTipsImg,
      videoPoster,
    },
  } = useAppContext();

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  // Start camera with best available (torch support preferred)
  const startCamera = useCallback(async () => {
    try {
      setError(null);
      setIsSearchingCamera(true);

      let cameraIdToUse = selectedCameraId;

      // If we already have a selected camera, use it directly
      if (!cameraIdToUse) {
        console.log('No camera selected yet, starting default camera first...');
        try {
          const betterCameraId = await findCameraWithTorch();

          if (betterCameraId) {
            cameraIdToUse = betterCameraId;
            setSelectedCameraId(cameraIdToUse);
          } else {
            // ios return '' for betterCameraId, so we need to use default camera
            // Start default camera (back camera)
            const initialConstraints: MediaStreamConstraints = {
              video: {
                facingMode: 'environment',
                width: { ideal: 1920 },
                height: { ideal: 1080 },
              },
            };

            const initialStream = await navigator.mediaDevices.getUserMedia(initialConstraints);
            const initialTrack = initialStream.getVideoTracks()[0];
            const initialDeviceId = initialTrack.getSettings().deviceId;

            cameraIdToUse = initialDeviceId || '';
            setSelectedCameraId(cameraIdToUse);
          }
        } catch (permissionErr) {
          console.error('Permission denied or camera not available:', permissionErr);
          throw permissionErr;
        }
      }

      if (!cameraIdToUse) {
        setError('No camera available');
        setIsSearchingCamera(false);
        return;
      }

      console.log(`Starting camera: ${cameraIdToUse}`);

      // Request camera access with the selected camera
      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: { exact: cameraIdToUse },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      // Check if torch is supported
      const videoTrack = stream.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities() as MediaTrackCapabilities & {
        torch?: boolean;
      };

      setIsTorchSupported(!!capabilities.torch);
      setIsCameraActive(true);
      // hack to avoid flickering image on iOS
      setTimeout(() => {
        setIsVideoVisible(true);
      }, 100);
      setIsSearchingCamera(false);

      console.log(
        `✓ Camera active: ${videoTrack.label}`,
        capabilities.torch ? '(with torch)' : '(no torch)'
      );
    } catch (err) {
      console.error('Error accessing camera:', err);
      const error = err as Error;
      setError(`Unable to access camera: ${error.message}`);
      setIsCameraActive(false);
      setIsVideoVisible(false);
      setIsSearchingCamera(false);
    }
  }, [selectedCameraId]);

  // Stop camera
  const stopCamera = useCallback(async () => {
    try {
      // First, turn off torch if it's on
      if (isTorchOn && streamRef.current) {
        try {
          const videoTrack = streamRef.current.getVideoTracks()[0];
          if (videoTrack) {
            await videoTrack.applyConstraints({
              // @ts-expect-error - torch is not in TypeScript definitions yet
              advanced: [{ torch: false }],
            });
            console.log('Torch turned off before stopping camera');
            // Give browser time to actually turn off the torch
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        } catch (torchErr) {
          console.error('Error turning off torch:', torchErr);
        }
      }

      // Clear video element first - stop rendering
      if (videoRef.current) {
        try {
          videoRef.current.pause();
          videoRef.current.srcObject = null;
        } catch (videoErr) {
          console.error('Error clearing video element:', videoErr);
        }
      }

      if (streamRef.current) {
        try {
          const tracks = streamRef.current.getTracks();
          console.log('Camera state before stop: tracks count:', tracks.length);

          // Stop all tracks only if they exist
          if (tracks.length > 0) {
            tracks.forEach((track) => {
              console.log(
                'Stopping track:',
                track.kind,
                track.label,
                'readyState:',
                track.readyState
              );
              track.stop();
            });
            console.log('All camera tracks stopped');

            // Give the browser time to release camera resources
            // This is important for proper cleanup
            await new Promise((resolve) => setTimeout(resolve, 200));

            console.log('Camera resources should be released now');
          } else {
            console.log('Camera not running, no tracks to stop');
          }

          // Clear stream reference
          streamRef.current = null;
        } catch (err) {
          console.error('Error stopping camera tracks:', err);
          // Try to force clear even if there was an error
          try {
            streamRef.current = null;
          } catch (clearErr) {
            console.error('Error clearing stream reference:', clearErr);
          }
        }
      }

      setIsCameraActive(false);
      setIsVideoVisible(false);
      setIsTorchOn(false);
    } catch (err) {
      console.error('Error in stopCamera:', err);
    }
  }, [isTorchOn]);

  // Toggle torch/flash
  const toggleTorch = async () => {
    if (!streamRef.current) return;

    try {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      await videoTrack.applyConstraints({
        // @ts-expect-error - torch is not in TypeScript definitions yet
        advanced: [{ torch: !isTorchOn }],
      });
      setIsTorchOn(!isTorchOn);
    } catch (err) {
      console.error('Error toggling torch:', err);
    }
  };

  // Crop image to square from center (scales down if larger than 1024px, then crops)
  const cropImageToSquare = (
    sourceImage: HTMLImageElement | HTMLVideoElement,
    canvas: HTMLCanvasElement
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const maxCropSize = 1024;
      const sourceWidth =
        sourceImage instanceof HTMLVideoElement ? sourceImage.videoWidth : sourceImage.width;
      const sourceHeight =
        sourceImage instanceof HTMLVideoElement ? sourceImage.videoHeight : sourceImage.height;

      // Step 1: Scale down if image is larger than maxCropSize
      let scaledWidth = sourceWidth;
      let scaledHeight = sourceHeight;
      const maxDimension = Math.max(sourceWidth, sourceHeight);

      if (maxDimension > maxCropSize) {
        // Scale proportionally so the longest side = maxCropSize
        const scale = maxCropSize / maxDimension;
        scaledWidth = Math.round(sourceWidth * scale);
        scaledHeight = Math.round(sourceHeight * scale);
      }

      // Step 2: Determine the crop size (square from center)
      const cropSize = Math.min(scaledWidth, scaledHeight);

      // Calculate center coordinates of scaled image
      const centerX = scaledWidth / 2;
      const centerY = scaledHeight / 2;

      // Calculate crop coordinates (top-left corner of the crop area)
      const cropX = Math.max(0, centerX - cropSize / 2);
      const cropY = Math.max(0, centerY - cropSize / 2);

      // Set canvas size to crop size
      canvas.width = cropSize;
      canvas.height = cropSize;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        // If we need to scale, first draw scaled image to a temporary canvas
        if (maxDimension > maxCropSize) {
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = scaledWidth;
          tempCanvas.height = scaledHeight;
          const tempCtx = tempCanvas.getContext('2d');

          if (tempCtx) {
            // Draw scaled image to temp canvas
            tempCtx.drawImage(sourceImage, 0, 0, scaledWidth, scaledHeight);

            // Then crop from temp canvas to final canvas
            ctx.drawImage(
              tempCanvas,
              cropX, // source x
              cropY, // source y
              cropSize, // source width
              cropSize, // source height
              0, // destination x
              0, // destination y
              cropSize, // destination width
              cropSize // destination height
            );
          }
        } else {
          // No scaling needed, crop directly from source
          const sourceCropX = Math.max(0, sourceWidth / 2 - cropSize / 2);
          const sourceCropY = Math.max(0, sourceHeight / 2 - cropSize / 2);

          ctx.drawImage(
            sourceImage,
            sourceCropX, // source x
            sourceCropY, // source y
            cropSize, // source width
            cropSize, // source height
            0, // destination x
            0, // destination y
            cropSize, // destination width
            cropSize // destination height
          );
        }

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const file = new File([blob], 'plate-scan.jpg', { type: 'image/jpeg' });
              resolve(file);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          },
          'image/jpeg',
          0.9
        );
      } else {
        reject(new Error('Failed to get canvas context'));
      }
    });
  };

  // Capture image from camera and navigate to details
  const handleScanPlate = async () => {
    if (!videoRef.current || !canvasRef.current || isScanning) return;

    try {
      setIsScanning(true);
      setError(null);
      const video = videoRef.current;
      const canvas = canvasRef.current;

      const file = await cropImageToSquare(video, canvas);

      await stopCamera();

      // Navigate to details page with the cropped image
      navigate(scanPlateDetailsPath, {
        state: {
          imageFile: file,
        },
      });
    } catch (error) {
      console.error('Error capturing image:', error);
      setError('Error capturing image. Please try again.');
      setIsScanning(false);
    } finally {
      setIsScanning(false);
    }
  };

  // Handle file upload from disk and navigate to details
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !canvasRef.current || isScanning) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    try {
      setIsScanning(true);
      setError(null);

      // Create image element to load the file
      const img = new Image();
      const canvas = canvasRef.current;

      img.onload = async () => {
        try {
          const croppedFile = await cropImageToSquare(img, canvas);

          await stopCamera();

          // Navigate to details page with the cropped image
          navigate(scanPlateDetailsPath, {
            state: {
              imageFile: croppedFile,
            },
          });
        } catch (error) {
          console.error('Error processing uploaded image:', error);
          setError('Error processing image. Please try again.');
          setIsScanning(false);
        } finally {
          // Reset file input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      };

      img.onerror = () => {
        setError('Failed to load image. Please try another file.');
        setIsScanning(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      };

      // Load the selected file
      img.src = URL.createObjectURL(file);
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Error uploading file. Please try again.');
      setIsScanning(false);
    } finally {
      setIsScanning(false);
    }
  };

  // Trigger file input click
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Start camera on mount
  useEffect(() => {
    let mounted = true;
    let cleanupCalled = false;

    const initCamera = async () => {
      // Small delay to avoid race conditions with React Strict Mode
      await new Promise((resolve) => setTimeout(resolve, 0));

      if (mounted && !cleanupCalled) {
        console.log('Initializing camera from useEffect');
        await startCamera();
      } else {
        console.log('Component unmounted before camera could start');
      }
    };

    initCamera();

    // Stop camera on navigation back or page unload
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      console.log('handleBeforeUnload called');
      stopCamera();
    };

    const handlePageHide = (event: PageTransitionEvent) => {
      console.log('handlePageHide called');
      stopCamera();
    };

    // Handle browser back/forward navigation (popstate)
    const handlePopState = () => {
      console.log('handlePopState called - user navigated back/forward');
      console.log('isCameraActive', isCameraActive);
      if (isCameraActive) {
        stopCamera();
      } else {
        if (window.location.pathname === '/tracker/scan-plate') {
          startCamera();
        } else {
          stopCamera();
        }
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('popstate', handlePopState);

    return () => {
      console.log('useEffect cleanup called - stopping camera');
      mounted = false;
      cleanupCalled = true;
      // Call stopCamera but don't await it (cleanup functions can't be async)
      // The function itself handles proper cleanup
      stopCamera();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('popstate', handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseClick = async () => {
    // Stop camera and wait for it to fully stop
    await stopCamera();

    // Give a small delay to ensure camera is fully released
    await new Promise((resolve) => setTimeout(resolve, 100));

    callNativeMobileApp({
      action: 'close_modal',
    });
    navigate(-1);
  };

  return (
    <>
      <div className="scan-plate">
        {/* Camera view - fullscreen */}
        <video
          ref={videoRef}
          className="camera-video-fullscreen"
          playsInline
          muted
          poster={videoPoster}
          style={{ opacity: isVideoVisible ? 1 : 0 }}
        />

        {/* Overlay with controls */}
        <div className="camera-overlay-fullscreen">
          {/* Scan area indicator */}
          {isCameraActive && <div className="scan-area" />}

          {/* Top bar with flash button */}

          <div className="top-controls">
            <button className="scan-plate-button" onClick={handleCloseClick}>
              <img src={closeWhiteIcon} alt="" />
            </button>
            <button className="scan-plate-button" onClick={toggleModal}>
              <img src={infoWhiteIcon} alt="" />
            </button>
          </div>

          {/* Bottom controls */}
          <div className="bottom-controls">
            {error ? (
              <div className="text-center">
                <Alert color="danger" className="mb-0" fade={false}>
                  <p className="mb-0 text-text-main-color text-start">
                    {t('tracker.scanFood.noAccessCamera.notification')}
                  </p>
                </Alert>

                {/* Restart button if not active */}
                {!isCameraActive && !isSearchingCamera && (
                  <Button
                    color="link"
                    size="sm"
                    onClick={() => window.location.reload()}
                    disabled={isSearchingCamera || isScanning}
                  >
                    <p>{t('tracker.scan.button.refreshCamera')}</p>
                  </Button>
                )}
              </div>
            ) : (
              <>
                {/* Torch button */}
                {isCameraActive && isTorchSupported ? (
                  <button className="scan-plate-button" onClick={toggleTorch} disabled={isScanning}>
                    <img src={isTorchOn ? flashOnIcon : flashOffIcon} alt="" />
                  </button>
                ) : (
                  <button className="scan-plate-button" disabled>
                    <img src={flashOffIcon} alt="" />
                  </button>
                )}

                {!isCameraActive && isSearchingCamera && (
                  <p>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {t('tracker.scan.findingCamera')}
                  </p>
                )}

                {isCameraActive && !isSearchingCamera && (
                  <button
                    className="scan-plate-button"
                    onClick={handleScanPlate}
                    disabled={isScanning}
                  >
                    <img src={cameraShutter} alt="" />
                  </button>
                )}

                {/* Upload from gallery button */}
                <button
                  className="scan-plate-button"
                  onClick={handleUploadClick}
                  disabled={isScanning || isSearchingCamera}
                >
                  <img src={photoGalleryIcon} alt="" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Hidden canvas for capturing */}
        <canvas ref={canvasRef} className="d-none" />

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="d-none"
          onChange={handleFileUpload}
        />
      </div>

      <Modal
        className="from-bottom"
        isOpen={showModal}
        fullscreen={true}
        size="lg"
        toggle={toggleModal}
      >
        <div className="select-insert-date">
          <Container>
            <Row>
              <Col xs="12" md={{ size: 4, offset: 4 }}>
                <img className="img-fluid" src={trackerScanFoodTipsImg} alt="" />
                <h2 className="title-medium bold">{t('tracker.tipsModal.title')}</h2>
                <p className="text-text-variant-color mb-xl">
                  {t('tracker.tipsModal.description.photo')}
                </p>

                <Button color="primary" className="w-100" onClick={toggleModal}>
                  {t('tracker.tipsModal.button')}
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </Modal>
    </>
  );
};

export default ScanPlateContainer;
